import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import useSeo from '../hooks/useSeo';
import HeroDashboard from '../components/HeroDashboard';
import ClientLogos from '../components/ClientLogos';
import StatsSection from '../components/StatsSection';
import SectionHeading from '../components/SectionHeading';
import ServicesGrid from '../components/ServicesGrid';
import ProcessSteps from '../components/ProcessSteps';
import PerformanceIntelligence from '../components/PerformanceIntelligence';
import GlobalGrowth from '../components/GlobalGrowth';
import IndustriesGrid from '../components/IndustriesGrid';
import CaseStudyCard from '../components/CaseStudyCard';
import TechnologySection from '../components/TechnologySection';
import IntegrationsSection from '../components/IntegrationsSection';
import WhyInterscope from '../components/WhyInterscope';
import DashboardDemo from '../components/DashboardDemo';
import CalculatorSection from '../components/CalculatorSection';
import AboutPreview from '../components/AboutPreview';
import Testimonials from '../components/Testimonials';
import CTA from '../components/CTA';
import { caseStudies } from '../mock/mock';

export default function Home() {
  useReveal('home');
  useSeo({
    title: 'Interscope Ads | Performance Marketing & Mobile User Acquisition Agency',
    description: 'Global performance marketing & mobile user acquisition agency. Acquire high-quality users, drive measurable growth and scale performance across global markets. Acquire. Optimize. Scale.',
  });
  return (
    <main>
      <HeroDashboard />
      <ClientLogos />
      <StatsSection />

      <section id="solutions" className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <SectionHeading eyebrow="What We Do" title="Solutions built for scale" subtitle="Full-funnel, technology-led services engineered around measurable business outcomes." />
        <ServicesGrid />
        <div className="reveal text-center mt-10">
          <Link to="/solutions" className="inline-flex items-center gap-2 rounded-full glass px-6 py-3 text-sm font-semibold text-slate-900 hover:border-[#2563eb]/50 transition-colors">
            Explore all solutions <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="relative max-w-7xl mx-auto px-5 md:px-8 pb-4">
        <SectionHeading eyebrow="How It Works" title="Acquire. Optimize. Scale." subtitle="A transparent, data-driven journey from strategy to scale." />
        <ProcessSteps />
      </section>

      <PerformanceIntelligence />
      <GlobalGrowth />

      <section id="industries" className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <SectionHeading eyebrow="Industries" title="Built for Different Growth Challenges" subtitle="Deep expertise across the verticals where performance matters most." />
        <IndustriesGrid />
      </section>

      {caseStudies.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-5 md:px-8 pb-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="reveal max-w-xl">
              <span className="inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">Success Stories</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">Case Studies</h2>
            </div>
            <Link to="/case-studies" className="reveal inline-flex items-center gap-2 rounded-full glass px-6 py-3 text-sm font-semibold text-slate-900 hover:border-[#2563eb]/50 transition-colors">View all <ArrowRight size={16} /></Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {caseStudies.slice(0, 6).map((cs) => (<CaseStudyCard key={cs.slug} cs={cs} />))}
          </div>
        </section>
      )}

      <TechnologySection />
      <IntegrationsSection />
      <WhyInterscope />
      <DashboardDemo />
      <CalculatorSection />
      <AboutPreview />

      <section className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <SectionHeading eyebrow="Client Love" title="Trusted by growth teams" subtitle="Brands across the globe rely on our data discipline and transparency." />
        <Testimonials />
      </section>

      <CTA />
    </main>
  );
}
